import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { KeyRound, ArrowLeft, Sparkles, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";

/** /auth/reset-password?token=... — sets a new password. */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("סיסמה חייבת להיות לפחות 6 תווים"); return; }
    if (pw !== confirm) { toast.error("הסיסמאות לא תואמות"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      // Auto-login → store token
      try { localStorage.setItem("flowup_auth_token", data.token); } catch { /* no-op */ }
      toast.success("סיסמה עודכנה — מעבירים אותך");
      setTimeout(() => {
        const u = data.user;
        navigate(u?.role === "super_admin" ? "/S.ADMIN" : "/login", { replace: true });
        // Force a hard refresh so AuthProvider picks up the new token
        window.location.reload();
      }, 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה באיפוס");
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" dir="rtl">
        <div className="max-w-sm bg-white rounded-2xl border border-rose-200 p-6 text-center space-y-3">
          <h1 className="font-heading font-bold text-lg text-rose-700">קישור לא תקין</h1>
          <p className="text-sm text-stone-600">לא נמצא token בכתובת. בקשי קישור איפוס חדש.</p>
          <Link to="/auth/forgot-password" className="inline-block text-brand font-bold hover:underline" data-testid="reset-back-forgot">
            בקשי קישור חדש
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-bl from-brand-50 via-white to-stone-50">
      <Toaster position="top-center" richColors dir="rtl" />
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline mb-6" data-testid="reset-back-login">
          <ArrowLeft className="h-3.5 w-3.5" />
          חזרה למסך התחברות
        </Link>
        <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-8 space-y-5" dir="rtl">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 bg-brand/10 text-brand px-3 py-1 rounded-full text-xs font-bold">
              <Sparkles className="h-3 w-3" />
              FlowUp
            </div>
            <h1 className="font-heading text-3xl font-bold">איפוס סיסמה</h1>
            <p className="text-sm text-stone-600">הזיני סיסמה חדשה</p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div>
              <Label>סיסמה חדשה</Label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} dir="ltr" value={pw} onChange={(e) => setPw(e.target.value)}
                  className="mt-1 text-right pl-9" required minLength={6} autoFocus data-testid="reset-new-pw" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute left-2 top-1/2 -translate-y-1/2 mt-0.5 text-stone-400 hover:text-stone-600" tabIndex={-1} data-testid="reset-new-toggle">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>אישור סיסמה</Label>
              <div className="relative">
                <Input type={showPw2 ? "text" : "password"} dir="ltr" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 text-right pl-9" required minLength={6} data-testid="reset-confirm-pw" />
                <button type="button" onClick={() => setShowPw2(!showPw2)} className="absolute left-2 top-1/2 -translate-y-1/2 mt-0.5 text-stone-400 hover:text-stone-600" tabIndex={-1} data-testid="reset-confirm-toggle">
                  {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
              data-testid="reset-submit-btn"
            >
              <KeyRound className="h-4 w-4 ml-2" />
              {busy ? "שומרת..." : "אפסי סיסמה"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
